'use client';

import { base64Image } from '@/constants';
import { PreKeyBannerType } from '@/enums';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

const ReactPlayer = dynamic(() => import('react-player/youtube'), { ssr: false });

const PreBanner = ({
  className = '',
  background,
  type,
  content,
  altText,
  dataTestId,
}: {
  className?: string;
  background?: string;
  type?: string;
  content?: any;
  altText?: string;
  dataTestId?: string;
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return (
    <div data-testid={dataTestId}>
      {type === PreKeyBannerType?.Video && (
        <div
          className={`relative overflow-hidden pb-8 text-center ${className} -mt-[calc(var(--header-height)_+_var(--preheader-height))] w-full pt-[calc(6rem_+_var(--header-height))] lg:-mt-[var(--header-height)] lg:pt-[calc(2rem_+_var(--header-height))]`}
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
          }}
          data-testid={`${dataTestId}-video-container`}
        >
          {mounted && background ? (
            <ErrorBoundary
              onError={(error) => {
                console.error('PreBanner: ReactPlayer error', error);
              }}
              fallback={null}
            >
              <ReactPlayer
                className="pre-banner-video-container"
                style={{
                  minWidth: '100%',
                  minHeight: '100%',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: -1,
                }}
                url={background}
                muted={true}
                loop={true}
                playing={true}
                config={{
                  playerVars: { showinfo: 0, disablekb: 0 },
                }}
                data-testid={`${dataTestId}-react-player`}
              />
            </ErrorBoundary>
          ) : (
            <span dangerouslySetInnerHTML={{ __html: content }} data-testid={`${dataTestId}-video-content`} />
          )}
        </div>
      )}
      {type === PreKeyBannerType?.Image && (
        <div
          className={`relative bg-transparent bg-cover bg-center p-8 text-center ${className} -mt-[calc(var(--preheader-height)+var(--announcement-bar-height)+var(--header-height))] pt-[8rem] lg:-mt-[calc(var(--preheader-height)+var(--announcement-bar-height)+var(--header-height))] lg:pt-[7rem]`}
          data-testid={`${dataTestId}-image-container`}
        >
          <div className="" dangerouslySetInnerHTML={{ __html: content }} data-testid={`${dataTestId}-image-content`} />
          <Image
            src={background || ''}
            style={{ objectFit: 'cover' }}
            fill
            alt={altText ? altText : 'Pre key banner'}
            className="z-[-1]"
            unoptimized={true}
            blurDataURL={base64Image}
            placeholder="blur"
            loading="eager"
            priority={true}
            data-testid={`${dataTestId}-image`}
          />
        </div>
      )}
      <style jsx global>{`
        .pre-banner-video-container {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100%;
          overflow: hidden;
          height: ${background?.includes('iframe') || background?.includes('vimeo') || background?.includes('youtu')
            ? '200vh !important'
            : '100% !important'};
        }
        .pre-banner-video-container video {
          /* Make video to at least 100% wide and tall */
          min-width: 100%;
          min-height: 1000vh;

          /* Setting width & height to auto prevents the browser from stretching or squishing the video */
          width: auto;
          height: auto;

          /* Center the video */
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      `}</style>
    </div>
  );
};

export default PreBanner;
